/**
 * AdvertisementPublicPage - Public page displaying an advertisement
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getAdvertisementByUuid, trackView, trackClick } from '../../services/advertisementsService';
import Menu from '../public/Menu';
import Footer from '../public/Footer';
import { useTranslation } from '../../context/LanguageContext';
import ReportAbuseModal from '../ui/ReportAbuseModal';
import { Flag, ExternalLink, Eye, User, Building2, Calendar } from 'lucide-react';

const FILES_BASE_URL = import.meta.env.VITE_FILES_BASE_URL || 'https://www.cabaza.be/files';

const AdvertisementPublicPage = () => {
    const { uuid } = useParams();
    const { t, language } = useTranslation();
    const ta = t.advertisements || {};
    const [advertisement, setAdvertisement] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showReportModal, setShowReportModal] = useState(false);

    useEffect(() => {
        const loadAdvertisement = async () => {
            if (!uuid) {
                setError('Invalid advertisement ID');
                setLoading(false);
                return;
            }

            try {
                const data = await getAdvertisementByUuid(uuid);
                if (data) {
                    setAdvertisement(data);
                    // Track view
                    trackView(data.id);
                } else {
                    setError('Advertisement not found');
                }
            } catch (err) {
                console.error('Error loading advertisement:', err);
                setError('Error loading advertisement');
            } finally {
                setLoading(false);
            }
        };

        loadAdvertisement();
    }, [uuid]);

    const handleLinkClick = () => {
        if (advertisement?.id) {
            trackClick(advertisement.id);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return null;
        try {
            const localeMap = {
                fr: 'fr-FR', en: 'en-GB', nl: 'nl-BE', de: 'de-DE',
                es: 'es-ES', it: 'it-IT', pt: 'pt-PT', el: 'el-GR'
            };
            return new Date(dateStr).toLocaleDateString(localeMap[language] || 'en-GB', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch {
            return null;
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
            </div>
        );
    }

    if (error || !advertisement) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
                <Menu onLogin={() => window.location.href = '/login'} />
                <div className="flex items-center justify-center min-h-[60vh]">
                    <div className="text-center">
                        <div className="text-6xl mb-4">🔍</div>
                        <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
                            {ta.public?.notFound || 'Advertisement not found'}
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            {ta.public?.notFoundDesc || 'This advertisement does not exist or is no longer available.'}
                        </p>
                        <Link
                            to="/"
                            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            ← {ta.public?.backToHome || 'Back to home'}
                        </Link>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <Menu onLogin={() => window.location.href = '/login'} />

            {/* Main Content */}
            <div className="max-w-4xl mx-auto px-4 py-12">
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden">
                    {/* Advertisement Image */}
                    {advertisement.imagePath && (
                        <div className="relative">
                            <img
                                src={`${FILES_BASE_URL}/${advertisement.imagePath}`}
                                alt={advertisement.title}
                                className="w-full h-auto max-h-[500px] object-contain bg-gray-100 dark:bg-gray-700"
                            />
                            {/* Type badge */}
                            <div className="absolute top-4 left-4">
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-black/60 text-white rounded-full text-sm backdrop-blur-sm">
                                    {advertisement.displayType === 'profile' ? (
                                        <>
                                            <User className="w-4 h-4" />
                                            {ta.public?.profileAd || 'Profile ad'}
                                        </>
                                    ) : (
                                        <>
                                            <Building2 className="w-4 h-4" />
                                            {advertisement.placeName || ta.public?.businessAd || 'Business ad'}
                                        </>
                                    )}
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Content */}
                    <div className="p-6 md:p-8">
                        {/* Title */}
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                            {advertisement.title}
                        </h1>

                        {/* Description */}
                        {advertisement.description && (
                            <p className="text-gray-600 dark:text-gray-300 text-lg leading-relaxed mb-6">
                                {advertisement.description}
                            </p>
                        )}

                        {/* Meta info */}
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-6">
                            <span className="flex items-center gap-1.5">
                                <Eye className="w-4 h-4" />
                                {advertisement.viewCount || 0} {ta.public?.views || 'views'}
                            </span>
                            {advertisement.startDate && (
                                <span className="flex items-center gap-1.5">
                                    <Calendar className="w-4 h-4" />
                                    {ta.public?.since || 'Since'} {formatDate(advertisement.startDate)}
                                </span>
                            )}
                        </div>

                        {/* Link button */}
                        {advertisement.linkUrl && (
                            <a
                                href={advertisement.linkUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={handleLinkClick}
                                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-800 transition-all shadow-lg mb-6"
                            >
                                <ExternalLink className="w-5 h-5" />
                                {ta.public?.visitLink || 'Visit link'}
                            </a>
                        )}

                        {/* Advertiser info */}
                        {advertisement.peopleUuid && (
                            <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
                                <Link
                                    to={`/people/${advertisement.peopleUuid}`}
                                    className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                >
                                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                                        {advertisement.firstName?.[0] || '?'}
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                            {ta.public?.publishedBy || 'Published by'}
                                        </p>
                                        <p className="font-semibold text-gray-900 dark:text-white">
                                            {advertisement.firstName} {advertisement.lastName}
                                        </p>
                                    </div>
                                </Link>
                            </div>
                        )}

                        {/* Report button */}
                        <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
                            <button
                                onClick={() => setShowReportModal(true)}
                                className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                            >
                                <Flag className="w-4 h-4" />
                                {ta.public?.reportAbuse || 'Report abuse'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />

            {/* Report Modal */}
            <ReportAbuseModal
                isOpen={showReportModal}
                onClose={() => setShowReportModal(false)}
                contentType="advertisement"
                contentId={advertisement?.id}
                contentUuid={advertisement?.uuid}
                contentTitle={advertisement?.title}
            />
        </div>
    );
};

export default AdvertisementPublicPage;
